import { useEffect, useRef } from 'react';

import { elevationService } from '../../services/ElevationService';
import { MAP_CONSTANTS } from './mapConstants';
import { LiveLineData } from './mapTypes';

const METERS_TO_DEGREES_LAT = 1 / 111320;
const METERS_TO_DEGREES_LON_AT_EQUATOR = 1 / 111320;

function calculateBoundsFromPosition(
  position: [number, number],
  radiusMeters: number
): { sw: [number, number]; ne: [number, number] } {
  const [lon, lat] = position;
  const latDelta = radiusMeters * METERS_TO_DEGREES_LAT;
  const lonDelta = (radiusMeters * METERS_TO_DEGREES_LON_AT_EQUATOR) / Math.cos((lat * Math.PI) / 180);

  return {
    sw: [lat - latDelta, lon - lonDelta] as [number, number],
    ne: [lat + latDelta, lon + lonDelta] as [number, number]
  };
}

function positionChanged(
  oldPosition: [number, number] | null,
  newPosition: [number, number],
  thresholdMeters: number
): boolean {
  if (!oldPosition) return true;

  const latDiff = Math.abs(oldPosition[1] - newPosition[1]) / METERS_TO_DEGREES_LAT;
  const lonDiff =
    Math.abs(oldPosition[0] - newPosition[0]) /
    (METERS_TO_DEGREES_LON_AT_EQUATOR / Math.cos((newPosition[1] * Math.PI) / 180));
  const distanceMeters = Math.sqrt(latDiff ** 2 + lonDiff ** 2);

  return distanceMeters > thresholdMeters;
}

export function useElevationPreload(liveLine: LiveLineData | undefined, mapLoaded: boolean) {
  const lastPreloadedPositionRef = useRef<[number, number] | null>(null);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!mapLoaded || !liveLine || liveLine.lines.length === 0) return;

    const preloadTilesForDrone = () => {
      const currentPosition = liveLine.lines[liveLine.lines.length - 1];
      const thresholdMeters = MAP_CONSTANTS.ELEVATION_PRELOAD_RADIUS_METERS * 0.5;

      if (!positionChanged(lastPreloadedPositionRef.current, currentPosition, thresholdMeters)) {
        return;
      }

      lastPreloadedPositionRef.current = currentPosition;

      const bounds = calculateBoundsFromPosition(currentPosition, MAP_CONSTANTS.ELEVATION_PRELOAD_RADIUS_METERS);

      const maxTiles = MAP_CONSTANTS.ELEVATION_PRELOAD_MAX_TILES;
      const bufferTiles = MAP_CONSTANTS.ELEVATION_PRELOAD_BUFFER_TILES;

      elevationService.preloadTiles(bounds, maxTiles, bufferTiles);
    };

    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
    timeoutIdRef.current = setTimeout(preloadTilesForDrone, MAP_CONSTANTS.ELEVATION_PRELOAD_DEBOUNCE_MS);

    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };
  }, [liveLine?.lines, mapLoaded]);
}
